import './style.css';
import {
  LIMITS,
  daysBetween,
  localDateOf,
  parseLocalDate,
  type Change,
  type LocalDate,
  type MeResponse,
  type MemberDto,
  type MemoryConflictBody,
  type MemoryDto,
  type PhotoDto,
  type SnapshotResponse,
  type SpaceDto,
  type SpacePatch,
} from '@star-sea/shared';
import { thumbHashToDataURL } from 'thumbhash';
import { Api, ApiError } from './api.ts';
import { FIELD_LABELS, FIELD_NAMES, changesOf, editNote, fieldsOf, isEmpty, rebase, toPatch, type FieldChanges, type FieldName, type MemoryFields } from './edit.ts';
import { fakeSnapshot } from './fake.ts';
import { Galaxy } from './galaxy.ts';
import { playIntro } from './intro.ts';
import { clearOffline, forgetThumbs, loadSnapshot, offlineStats, saveSnapshot } from './offline.ts';
import { createSound } from './sound.ts';
import { Stardust } from './stardust.ts';
import type { CardImage } from './carousel.ts';
import {
  confirmDialog,
  errorScreen,
  inviteDialog,
  loadingScreen,
  memoryEditor,
  mountCaption,
  mountHud,
  mountIntro,
  mountIntroWords,
  mountPanel,
  mountShell,
  pairScreen,
  setupScreen,
  spaceDialog,
  swatchCanvas,
  toast,
  trashDialog,
  type MemoryEditor,
  type PanelContent,
  type ToastHandle,
} from './ui.ts';
import { deviceLabel, isHeic, uploadForm } from './upload.ts';
import { defaultWords } from './words.ts';

const api = new Api();
const appRoot = document.getElementById('app')!;
const shell = mountShell(appRoot);
const sound = createSound();
const params = new URLSearchParams(location.search);
const fakeCount = Math.min(200_000, Math.max(0, Math.floor(Number(params.get('fake') ?? 0)) || 0));
const showStats = import.meta.env.DEV || fakeCount > 0 || params.has('stats');
const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
// 手机和小屏：照片切换的形变粒子少一些（同原型）
const lowEnd = matchMedia('(pointer: coarse)').matches || Math.min(innerWidth, innerHeight) < 560;
// ?intro=0 跳过入场动画（压测、调试时用）
const introOn = params.get('intro') !== '0';
// 画质档位（quality.ts）：按设备记住自动调到的那档；?quality=N 固定在第 N 档（调试用，不记住）
const QUALITY_KEY = 'star-sea/quality';
const forcedQuality = params.has('quality') ? Number(params.get('quality')) || 0 : null;

function readQuality(): number {
  try {
    return Number(localStorage.getItem(QUALITY_KEY)) || 0;
  } catch {
    return 0;
  }
}

function saveQuality(level: number): void {
  try {
    localStorage.setItem(QUALITY_KEY, String(level));
  } catch {
    // 浏览器不让存：下次从头调
  }
}

const WEEKDAY = '日一二三四五六';
/** 星轨开关按设备记住（同原型）；默认开 */
const TRAIL_KEY = 'star-sea/trail';

function readTrail(): boolean {
  try {
    return localStorage.getItem(TRAIL_KEY) !== '0';
  } catch {
    return true;
  }
}

function saveTrail(on: boolean): void {
  try {
    localStorage.setItem(TRAIL_KEY, on ? '1' : '0');
  } catch {
    // 浏览器不让存：这次打开时有效
  }
}

function coordinates(lat: number, lng: number): string {
  return `${lat >= 0 ? '北纬' : '南纬'} ${Math.abs(lat).toFixed(3)}° · ${lng >= 0 ? '东经' : '西经'} ${Math.abs(lng).toFixed(3)}°`;
}

function dayText(date: LocalDate): string {
  const { y, m, d } = parseLocalDate(date);
  return `${y} 年 ${m} 月 ${d} 日`;
}

/** 改回忆撞上 Ta 刚改过（409）时，服务端附带的最新版本 */
function conflictOf(e: unknown): MemoryDto | null {
  if (!(e instanceof ApiError) || e.status !== 409 || !e.body || typeof e.body !== 'object' || !('memory' in e.body)) return null;
  return (e.body as MemoryConflictBody).memory;
}

function showScreen(node: HTMLElement | null): void {
  shell.overlay.replaceChildren(...(node ? [node] : []));
}

/** 邀请链接形如 …/#invite=CODE；码放在 # 后面，不会发到服务器、也不会进访问日志 */
function takeInviteFromHash(): string {
  const m = /^#invite=([0-9A-Za-z-]+)$/.exec(location.hash);
  return m ? m[1]! : '';
}

function clearHash(): void {
  if (location.hash) history.replaceState(null, '', location.pathname + location.search);
}

/** 这台设备不能再用了（令牌失效、被移除）：令牌和离线缓存一起清掉 */
async function forget(): Promise<void> {
  api.setToken(null);
  await clearOffline();
}

/** 连不上服务时，用上次同步的快照打开星海（offline.ts）；没有缓存返回 false */
async function startOffline(): Promise<boolean> {
  if (!api.token) return false;
  const cached = await loadSnapshot();
  if (!cached) return false;
  clearHash();
  start(cached.snapshot, cached.me, { offlineSince: cached.savedAt });
  return true;
}

async function boot(): Promise<void> {
  if (fakeCount > 0) return start(fakeSnapshot(fakeCount), null);
  showScreen(loadingScreen());
  let setup: boolean;
  try {
    setup = (await api.health()).setup;
  } catch {
    // 有上次同步的星海就先显示它，连上以后自动跟上
    if (await startOffline()) return;
    // 设备明确没联网时直接说；联着网也可能上不了外网，所以另一种情况不下结论，原因都列上
    showScreen(
      navigator.onLine === false
        ? errorScreen('这台设备没有联网', '连上 Wi-Fi 或打开移动数据，再点「重试」。', () => void boot())
        : errorScreen('连不上星海服务', '星海服务没有回应：可能是网络不太好，也可能是服务正在更新、或者暂时停了。等一会儿再重试；一直连不上的话，请维护星海的那个人看看服务开着没有。', () => void boot()),
    );
    return;
  }
  if (!setup) {
    // 服务那边还没有星海（比如重新装过）：旧的令牌和离线缓存都不再有用
    await forget();
    showScreen(
      setupScreen(async (v) => {
        const s = await api.setup({ ...v, deviceLabel: deviceLabel() });
        api.setToken(s.token);
        await enter();
      }),
    );
    return;
  }
  if (!api.token) return showPair();
  await enter();
}

function showPair(): void {
  const invite = takeInviteFromHash();
  showScreen(
    pairScreen(invite, async (v) => {
      const s = await api.pair({ code: v.code, displayName: v.displayName || undefined, deviceLabel: deviceLabel() });
      api.setToken(s.token);
      clearHash();
      await enter();
    }),
  );
}

async function enter(): Promise<void> {
  try {
    const [me, snap] = await Promise.all([api.me(), api.snapshot()]);
    clearHash();
    void saveSnapshot(me, snap);
    start(snap, me);
  } catch (e) {
    if (e instanceof ApiError && e.status === 401) {
      await forget();
      return showPair();
    }
    // 健康检查过了、取快照时又断了：同样先显示离线缓存
    if (e instanceof ApiError && (e.status === 0 || e.status >= 500) && (await startOffline())) return;
    showScreen(errorScreen('打开星海时出错了', e instanceof Error ? e.message : String(e), () => void boot()));
  }
}

/** offlineSince：连不上服务，用的是这个时间存下的离线缓存 */
function start(snap: SnapshotResponse, me: MeResponse | null, opts: { offlineSince?: string } = {}): void {
  showScreen(null);
  const fake = me === null;
  const members = new Map<string, MemberDto>(snap.members.map((m) => [m.id, m]));
  const memories = new Map<string, MemoryDto>(snap.memories.map((m) => [m.id, m]));
  /** 星海的名字和在一起的日子，可以改（applySpace）；spaceSeq 是它对应的变更序号 */
  let space = snap.space;
  let spaceSeq = snap.seq;
  const galaxy = new Galaxy(shell.stage, shell.labels, {
    reducedMotion,
    thumbs: { fake, token: api.token },
    quality: forcedQuality !== null ? { start: forcedQuality, fixed: true } : { start: readQuality(), fixed: false, onChange: saveQuality },
  });
  // 有入场动画时，星一开始就在爱心上，不再单独做「依次点亮」
  galaxy.load(snap, !introOn);
  // 星尘：照片面板打开、关上，和入场那段话飘走都用它（一块全屏透明画布，第一次用时才建）
  // 粒子化的格点：桌面 128×128、手机 96×96（同原型）
  const dust = new Stardust(shell.fx, { grid: lowEnd ? 96 : 128 });
  /** 入场那段话：没写就用默认的「在一起的第 N 天 / 已经点亮了 M 颗星」（这里也是星海设置里的提示文字） */
  const introWords = () => space.introText || defaultWords(daysBetween(space.since, localDateOf(new Date())) + 1, galaxy.count);
  let introPlaying = introOn;
  if (introOn) {
    appRoot.classList.add('intro-on');
    const view = mountIntro(shell.overlay, { title: snap.space.name, sub: '我们的每一段回忆，都是一颗星' });
    // 离线缓存里的老快照可能还没有这一项
    const hold = space.introHold ?? LIMITS.introHold;
    const words = hold > 0 ? { view: mountIntroWords(shell.overlay, { text: introWords(), dust, reduced: reducedMotion }), hold } : undefined;
    void playIntro(galaxy, view, sound, { reduced: reducedMotion, onReveal: () => appRoot.classList.remove('intro-on'), words }).then(() => (introPlaying = false));
  }
  // 浏览器要先有一次点击才能出声：第一次点击或按键时解锁并开始放背景音乐。
  // 入场期间由「轻触心跳」解锁，音乐从爆炸开始淡入
  const kick = () => {
    sound.unlock();
    if (introPlaying) return;
    sound.start(2.5);
    removeEventListener('pointerdown', kick, true);
    removeEventListener('keydown', kick, true);
  };
  addEventListener('pointerdown', kick, true);
  addEventListener('keydown', kick, true);

  const hud = mountHud(
    shell.overlay,
    {
      onHome: () => {
        exitRing(false);
        galaxy.select(-1, false);
        galaxy.frameAll(true);
      },
      onFiles: (files) => {
        if (offline) toast(shell.toasts, '现在连不上星海服务，连上以后再添照片');
        else void upload(files);
      },
      onInvite: () => inviteDialog({ canInviteMember: members.size < 2, create: (kind) => api.invite(kind) }),
      onTrail: () => {
        trailOn = !trailOn;
        saveTrail(trailOn);
        galaxy.setTrail(trailOn);
        hud.setTrail(trailOn);
        toast(shell.toasts, trailOn ? '星轨已显示：按时间连起所有回忆' : '星轨已隐藏');
      },
      onSettings: () => openSettings(),
      music: sound,
      cache: { stats: offlineStats, clear: clearOffline },
    },
    { fake, stats: showStats },
  );
  /** 打开时连不上服务、显示的是离线缓存；实时连接接上后变回 false */
  let offline = opts.offlineSince !== undefined;
  let offlineNote: ReturnType<typeof toast> | null = null;
  if (opts.offlineSince) {
    const d = new Date(opts.offlineSince);
    const at = `${d.getMonth() + 1} 月 ${d.getDate()} 日 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    hud.setConnection('offline');
    offlineNote = toast(shell.toasts, `现在连不上星海服务，先显示 ${at} 同步的星海；连上以后会自动跟上。`, { sticky: true });
  }
  let trailOn = readTrail();
  galaxy.setTrail(trailOn);
  hud.setTrail(trailOn);
  hud.setTitle(snap.space.name);
  const refreshCounts = () => {
    const days = daysBetween(space.since, localDateOf(new Date())) + 1;
    hud.setSub(`${fake ? '压测模式 · 没有照片 · ' : ''}在一起的第 ${days.toLocaleString('zh-CN')} 天 · 已点亮 ${galaxy.count.toLocaleString('zh-CN')} 颗星`);
  };
  refreshCounts();
  setInterval(() => {
    const s = galaxy.stats;
    hud.setStats(`${s.fps.toFixed(0)} fps · ${s.frameMs.toFixed(1)} ms · ${s.stars.toLocaleString('zh-CN')} 颗星 · ${s.calls} 次绘制 · 画质 ${galaxy.qualityLevel}`);
  }, 500);

  // ── 照片面板 ──
  let loading: AbortController | null = null;
  const step = (delta: number) => {
    const i = galaxy.selectedIndex;
    const day = galaxy.sameDay(i);
    const k = day.indexOf(i);
    if (k >= 0 && day.length > 1) galaxy.select(day[(k + delta + day.length) % day.length]!);
  };
  /** 一颗星在面板和轮转字幕里显示的文字 */
  const describe = (i: number): PanelContent => {
    const p = galaxy.photos[i]!;
    const memory = memories.get(p.memoryId);
    const day = galaxy.sameDay(i);
    const d = new Date(`${p.date}T12:00:00`);
    const time = p.takenAt && /T(\d{2}:\d{2})/.exec(p.takenAt)?.[1];
    const nth = daysBetween(space.since, p.date) + 1;
    return {
      date: `${d.getFullYear()} 年 ${d.getMonth() + 1} 月 ${d.getDate()} 日 · 星期${WEEKDAY.charAt(d.getDay())}${time ? ` · ${time}` : ''}`,
      title: memory?.title ?? (nth > 0 ? `在一起的第 ${nth} 天` : '认识之前'),
      place: memory?.place?.name ?? (p.lat !== undefined && p.lng !== undefined ? coordinates(p.lat, p.lng) : ''),
      story: memory?.story ?? '',
      by: `由 ${members.get(p.createdBy)?.displayName ?? 'Ta'} 点亮${memory?.title && nth > 0 ? ` · 在一起的第 ${nth} 天` : ''}`,
      count: `${day.indexOf(i) + 1} / ${day.length}`,
      aspect: p.width && p.height ? p.width / p.height : 4 / 3,
      accent: p.color,
      placeholder: p.thumbhash ? thumbHashToDataURL(Uint8Array.from(atob(p.thumbhash), (c) => c.charCodeAt(0))) : undefined,
      swatch: fake ? `radial-gradient(circle at 30% 30%, ${p.color}, #0b0d1a)` : undefined,
      note: fake ? '压测数据没有照片' : p.thumbs ? undefined : 'HEIC 原图已经存好，服务端暂时解不开，缩略图以后补上',
      editable: !fake,
    };
  };

  // ── 轮转：这一天的照片排成光环 ──
  const caption = mountCaption(shell.overlay, { onBack: () => exitRing(true) });
  /** 光环上是哪些星（其中一张被删了就收起光环） */
  let ringDay: number[] = [];
  /** 从光环回到面板时不放星尘：照片刚才就在眼前 */
  let quietOpen = false;
  const cardPlaceholder = async (i: number): Promise<CardImage | null> => {
    const p = galaxy.photos[i]!;
    if (fake) return swatchCanvas(p.color);
    if (!p.thumbhash) return null;
    const img = new Image();
    img.src = thumbHashToDataURL(Uint8Array.from(atob(p.thumbhash), (c) => c.charCodeAt(0)));
    await img.decode();
    return img;
  };
  const cardImage = async (i: number, signal: AbortSignal): Promise<CardImage | null> => {
    const p = galaxy.photos[i]!;
    if (fake || !p.thumbs) return null;
    const url = URL.createObjectURL(await api.photoBlob(p.id, 't512', signal));
    const img = new Image();
    img.src = url;
    try {
      await img.decode();
    } catch (e) {
      URL.revokeObjectURL(url);
      throw e;
    }
    return img;
  };
  const enterRing = () => {
    const i = galaxy.selectedIndex;
    if (i < 0 || galaxy.inCarousel) return;
    const day = galaxy.sameDay(i);
    if (day.length < 2) return;
    closeEditor(true);
    panel.hide({ quiet: true });
    appRoot.classList.add('ring-on');
    ringDay = day;
    galaxy.enterCarousel(day, day.indexOf(i), {
      placeholder: cardPlaceholder,
      load: cardImage,
      onFront: (j, k, n) => {
        const c = describe(j);
        caption.show({ date: c.date, title: c.title, place: c.place, count: `${k + 1} / ${n}` });
      },
      onOpen: () => exitRing(true),
    });
  };
  /** 收起光环；open 时回到正面那张的照片面板 */
  const exitRing = (open: boolean) => {
    if (!galaxy.inCarousel) return;
    const i = galaxy.leaveCarousel();
    ringDay = [];
    caption.hide();
    appRoot.classList.remove('ring-on');
    if (open && i >= 0) {
      quietOpen = true;
      galaxy.select(i);
    }
  };
  /** 飞到某颗星（提示里的「去看看」）；在光环里时先收起 */
  const goTo = (i: number) => {
    exitRing(false);
    galaxy.select(i);
  };

  const panel = mountPanel(
    shell.overlay,
    {
      onClose: () => galaxy.select(-1, false),
      onPrev: () => step(-1),
      onNext: () => step(1),
      onRing: enterRing,
      onEdit: (focus) => openEditor(focus),
      onDelete: () => void deleteShown(),
    },
    { morphPoints: lowEnd ? 24576 : 65536, dust, reduced: reducedMotion },
  );
  const narrow = matchMedia('(max-width: 640px)');
  /** 面板里正显示的那颗星 */
  let shown = -1;
  galaxy.onSelect = (i) => {
    // 换到了别的回忆的照片、或者关了面板：编辑到一半的先收起来，下次打开接着写
    if (editing && (i < 0 || galaxy.photos[i]?.memoryId !== editing.memoryId)) closeEditor(true);
    loading?.abort();
    loading = null;
    galaxy.setViewShift(i >= 0 && narrow.matches ? 0.24 : 0);
    const before = shown;
    shown = i;
    const quiet = quietOpen;
    quietOpen = false;
    if (i < 0) return panel.hide();
    const p = galaxy.photos[i]!;
    // 面板开着时换到另一张：等清晰图到了用流体形变换过去
    const sw = panel.open && before >= 0 && before !== i && !fake && p.thumbs ? { phase: (before * 1.71 + i * 0.93) % 10 } : undefined;
    // 新打开时星尘从星里飞进画框；从光环回来时照片刚才就在眼前，直接显示
    panel.show(describe(i), { sw, from: sw || quiet ? undefined : () => galaxy.screenPosition(i) });
    if (!fake && p.thumbs) {
      const ctrl = (loading = new AbortController());
      api
        .photoBlob(p.id, 't512', ctrl.signal)
        .then((b) => {
          if (galaxy.selectedIndex === i) panel.setImage(b);
        })
        .catch(() => undefined);
    }
  };

  addEventListener('keydown', (e) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    // 对话框（邀请、星海设置）里的按键归对话框
    if (e.target instanceof Element && e.target.closest('dialog')) return;
    if (galaxy.inCarousel) {
      // 回车只在没有按钮获得焦点时算「打开正面那张」，免得和按钮自己的回车撞上（同原型）
      if (e.key === 'Escape' || (e.key === 'Enter' && (e.target === document.body || e.target === galaxy.renderer.domElement))) exitRing(true);
      else if (e.key === 'ArrowLeft') galaxy.carouselStep(-1);
      else if (e.key === 'ArrowRight') galaxy.carouselStep(1);
      else if (e.key === 'Home') {
        exitRing(false);
        galaxy.frameAll(true);
      }
      return;
    }
    if (e.key === 'Escape' && panel.open) galaxy.select(-1, false);
    // 没开着照片：Esc 一层一层往外退，一天 → 一年 → 整片星海
    else if (e.key === 'Escape') galaxy.stepBack();
    else if (e.key === 'Home') galaxy.frameAll(true);
    else if (panel.open && e.key === 'ArrowLeft') step(-1);
    else if (panel.open && e.key === 'ArrowRight') step(1);
  });

  // 有变化时过几秒存一份新的快照，下次连不上服务时显示的就是最新的
  let seq = snap.seq;
  let saveTimer: ReturnType<typeof setTimeout> | undefined;
  const remember = () => {
    if (!me) return;
    clearTimeout(saveTimer);
    saveTimer = setTimeout(() => void saveSnapshot({ ...me, space }, { seq, space, members: [...members.values()], memories: [...memories.values()], photos: galaxy.livePhotos() }), 3000);
  };

  const nameOf = (id: string) => members.get(id)?.displayName ?? 'Ta';
  /** 改东西的是谁：「小满 」或者「你在另一台设备上」；mid 是放在句子中间（名字两边都空一格，同「由 小满 点亮」） */
  const actor = (id: string, mid = false) => (id === me?.member.id ? '你在另一台设备上' : mid ? ` ${nameOf(id)} ` : `${nameOf(id)} `);

  // ── 编辑回忆（edit.ts）──
  interface Editing {
    memoryId: string;
    /** 打开编辑时看到的版本；撞上 Ta 刚改过、合并以后换成最新的 */
    base: MemoryDto;
    form: MemoryEditor;
    saving: boolean;
  }
  let editing: Editing | null = null;
  /** 没保存就被打断（点了别的星、关了面板、进了轮转）的修改：再打开这段回忆的编辑时接着写 */
  const drafts = new Map<string, { base: MemoryDto; values: MemoryFields }>();

  /**
   * 回忆来了或者改了（自己保存的、实时推送的、上传带回来的）。推送可能比保存的回应晚到，
   * 旧版本不能盖掉新版本：updatedAt 是服务端给的，同一段回忆每改一次都更晚。
   */
  const applyMemory = (m: MemoryDto): boolean => {
    const old = memories.get(m.id);
    if (old && old.updatedAt > m.updatedAt) return false;
    memories.set(m.id, m);
    galaxy.setMemory(m);
    const i = galaxy.selectedIndex;
    if (i >= 0 && galaxy.photos[i]?.memoryId === m.id) panel.text(describe(i));
    // 正在编辑这段回忆、又不是自己这次保存的：还没动过表单就换成最新的，动过了先提醒一句
    const e = editing;
    if (e && e.memoryId === m.id && !e.saving && m.updatedAt !== e.base.updatedAt) {
      if (isEmpty(changesOf(fieldsOf(e.base), e.form.values()))) {
        e.base = m;
        e.form.set(fieldsOf(m));
        e.form.note(`${actor(m.updatedBy)}刚改过这段回忆，表单里已经是最新的`);
      } else e.form.note(`${actor(m.updatedBy)}刚刚也改了这段回忆；保存时要是和你改的地方撞上，会先给你看改了什么`);
    }
    return true;
  };

  const openEditor = (focus?: FieldName) => {
    const i = galaxy.selectedIndex, p = galaxy.photos[i], m = p && memories.get(p.memoryId);
    if (fake || !p || !m || editing) return;
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再写');
    const draft = drafts.get(m.id);
    drafts.delete(m.id);
    const nth = daysBetween(space.since, p.date) + 1;
    const form = memoryEditor({
      values: draft?.values ?? fieldsOf(m),
      placeholders: { title: nth > 0 ? `在一起的第 ${nth} 天` : '认识之前', place: p.lat !== undefined && p.lng !== undefined ? coordinates(p.lat, p.lng) : '' },
      note: draft ? `接着上次没保存的修改${draft.base.updatedAt !== m.updatedAt ? `；这期间${actor(m.updatedBy, true)}也改过，保存时会先给你看改了什么` : ''}` : undefined,
      dirty: (v) => !isEmpty(changesOf(fieldsOf(e.base), v)),
      onSave: () => void save(e),
      onCancel: () => closeEditor(false),
    });
    const e: Editing = { memoryId: m.id, base: draft?.base ?? m, form, saving: false };
    editing = e;
    panel.edit(form.node);
    form.focus(focus);
  };

  /** stash：被打断的，把没保存的修改收起来 */
  const closeEditor = (stash: boolean) => {
    const e = editing;
    if (!e) return;
    editing = null;
    const values = e.form.values();
    if (stash && !isEmpty(changesOf(fieldsOf(e.base), values))) drafts.set(e.memoryId, { base: e.base, values });
    panel.edit(null);
  };

  const finish = (e: Editing, m: MemoryDto) => {
    applyMemory(m);
    remember();
    drafts.delete(e.memoryId);
    if (editing === e) closeEditor(false);
  };

  /** 合并到 latest 上：我没动过的字段换成最新的，免得保存时把 Ta 改的又改回去 */
  const rebaseForm = (e: Editing, latest: MemoryDto, mine: FieldChanges) => {
    const theirs = fieldsOf(latest);
    e.form.set(Object.fromEntries(FIELD_NAMES.filter((k) => mine[k] === undefined).map((k) => [k, theirs[k]])));
    e.base = latest;
  };

  /** 同一个字段两个人写得不一样：给人看 Ta 写的，选用谁的 */
  const askConflict = (e: Editing, latest: MemoryDto, mine: FieldChanges, conflicts: FieldName[]) => {
    // 表单已经收起来了：草稿还在，下次打开时会提醒
    if (editing !== e) return;
    const theirs = fieldsOf(latest), who = actor(latest.updatedBy);
    e.form.conflict({
      text: `${who}刚刚也改了${conflicts.map((k) => `「${FIELD_LABELS[k]}」`).join('')}，和你写的不一样。下面是${latest.updatedBy === me?.member.id ? '那台设备上' : ' Ta '}写的，你写的还在表单里。`,
      theirs: conflicts.map((k) => ({ label: FIELD_LABELS[k], value: String(theirs[k]) })),
      onMine: () => {
        e.form.conflict(null);
        rebaseForm(e, latest, mine);
        void save(e);
      },
      onTheirs: () => {
        e.form.conflict(null);
        rebaseForm(e, latest, mine);
        e.form.set(Object.fromEntries(conflicts.map((k) => [k, theirs[k]])));
        e.form.note('换成了 Ta 写的；你改的别的地方还在，看过没问题再保存');
      },
    });
  };

  const save = async (e: Editing) => {
    if (e.saving) return;
    let base = e.base, mine = changesOf(fieldsOf(base), e.form.values());
    if (isEmpty(mine)) return closeEditor(false);
    e.saving = true;
    e.form.busy(true);
    e.form.error('');
    try {
      // 撞上 Ta 改的是别的字段：在最新版本上自动重试；连着撞三次就停下，让人再点一次
      for (let attempt = 0; ; attempt++) {
        try {
          return finish(e, (await api.updateMemory(e.memoryId, toPatch(base.updatedAt, mine))).memory);
        } catch (err) {
          const latest = conflictOf(err);
          if (!latest || attempt >= 2) throw err;
          applyMemory(latest);
          const r = rebase(fieldsOf(base), mine, fieldsOf(latest));
          if (isEmpty(r.changes)) return finish(e, latest);
          if (r.conflicts.length) return askConflict(e, latest, mine, r.conflicts);
          base = latest;
          mine = r.changes;
        }
      }
    } catch (err) {
      e.form.error(err instanceof ApiError && err.status === 0 ? '网络断了，没保存上；连上以后再点一次保存' : err instanceof Error ? err.message : String(err));
    } finally {
      e.saving = false;
      e.form.busy(false);
    }
  };

  // ── 星海设置：名字、在一起的日子 ──
  /**
   * 改了（自己保存的回应或者推送来的）：日子变了，星按新的螺线重新排。
   * 星海没有版本号，按变更序号比先后：保存的回应和推送谁先到不一定，晚到的旧版本不能盖掉新的
   */
  const applySpace = (next: SpaceDto, by: string | null, seqOf: number) => {
    if (seqOf <= spaceSeq) return;
    spaceSeq = seqOf;
    const old = space;
    const reworded = old.introText !== next.introText || old.introHold !== next.introHold;
    if (old.name === next.name && old.since === next.since && !reworded) return;
    space = next;
    hud.setTitle(next.name);
    const moved = old.since !== next.since;
    if (moved) {
      exitRing(false);
      galaxy.setSince(next.since);
      refreshCounts();
      const i = galaxy.selectedIndex;
      if (i >= 0) panel.text(describe(i));
    }
    remember();
    if (by === me?.member.id) {
      if (moved) toast(shell.toasts, `在一起的日子改成了 ${dayText(next.since)}，星海按新的日子重新排好了`);
      return;
    }
    const name = by ? nameOf(by) : 'Ta';
    if (moved && old.name !== next.name) toast(shell.toasts, `${name} 改了星海的名字和在一起的日子（${dayText(next.since)}），星海重新排了一遍`, { ms: 8000 });
    else if (moved) toast(shell.toasts, `${name} 把在一起的日子改成了 ${dayText(next.since)}，星海重新排了一遍`, { ms: 8000 });
    else if (old.name !== next.name) toast(shell.toasts, `${name} 把星海的名字改成了「${next.name}」`);
    // 那段话下次打开时才看得到，先说一声
    else if (reworded) toast(shell.toasts, `${name} 改了入场的那段话，下次打开星海时就能看到`);
  };

  const openSettings = () => {
    if (fake) return;
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再改');
    const hold = space.introHold ?? LIMITS.introHold;
    spaceDialog({
      name: space.name,
      since: space.since,
      introText: space.introText ?? '',
      introHold: hold,
      introPlaceholder: defaultWords(daysBetween(space.since, localDateOf(new Date())) + 1, galaxy.count),
      save: async (v) => {
        const patch: SpacePatch = {};
        if (v.name !== space.name) patch.name = v.name;
        if (v.since !== space.since) patch.since = v.since;
        if (v.introText !== (space.introText ?? '')) patch.introText = v.introText || null;
        if (v.introHold !== hold) patch.introHold = v.introHold;
        if (!Object.keys(patch).length) return;
        const res = await api.updateSpace(patch);
        applySpace(res.space, me?.member.id ?? null, res.seq);
      },
      onTrash: () => openTrash(),
    });
  };

  // ── 删除和回收站（服务端 trash.ts）：两个人都能删、都能恢复 ──
  /** 回收站开着时：Ta 那边删了、恢复了，重新列一遍 */
  let trashView: { refresh(): void } | null = null;
  let trashTimer: ReturnType<typeof setTimeout> | undefined;
  const refreshTrash = () => {
    if (!trashView) return;
    clearTimeout(trashTimer);
    trashTimer = setTimeout(() => trashView?.refresh(), 300);
  };

  /** 同一个人接连删、接连恢复的照片合成一条提示，10 秒内来的算一组 */
  interface Tally {
    by: string;
    ids: string[];
    note: ToastHandle | null;
    timer?: ReturnType<typeof setTimeout>;
  }
  const tallies = new Map<'bin' | 'back', Tally>();
  const tally = (kind: 'bin' | 'back', by: string, id: string, render: (ids: string[]) => { text: string; action: { label: string; onClick: () => void } }) => {
    let t = tallies.get(kind);
    if (!t || t.by !== by) tallies.set(kind, (t = { by, ids: [], note: null }));
    t.ids.push(id);
    const group = t, { text, action } = render([...t.ids]);
    // 点了提示里的按钮，这一组就结束了，再来的另起一条
    const act = {
      label: action.label,
      onClick: () => {
        if (tallies.get(kind) === group) tallies.delete(kind);
        action.onClick();
      },
    };
    if (t.note) t.note.done(text, act, 10_000);
    else t.note = toast(shell.toasts, text, { action: act, ms: 10_000 });
    clearTimeout(t.timer);
    t.timer = setTimeout(() => tallies.get(kind) === group && tallies.delete(kind), 10_000);
  };

  /** 这一天的照片多了、少了：面板里「第几张 / 共几张」和轮转按钮跟着变 */
  const refreshPanel = () => {
    const s = galaxy.selectedIndex;
    if (s >= 0 && panel.open) panel.text(describe(s));
  };

  /** 照片放进了回收站（自己删的或者推送来的）：星熄灭；面板正显示它就换到这一天的下一张，没有了就关上 */
  const applyDeleted = (id: string): boolean => {
    const i = galaxy.indexOf(id);
    if (i < 0 || galaxy.isGone(i)) return false;
    const day = galaxy.sameDay(i), shownNow = galaxy.selectedIndex === i;
    if (galaxy.inCarousel && ringDay.includes(i)) exitRing(false);
    galaxy.remove(id);
    if (shownNow) {
      const rest = day.filter((k) => k !== i);
      if (rest.length) galaxy.select(rest[Math.min(day.indexOf(i), rest.length - 1)]!);
      else galaxy.select(-1, false);
    } else refreshPanel();
    refreshCounts();
    remember();
    return true;
  };

  /** 从回收站恢复了（「撤销」「恢复」、重传，或者推送来的）：星在原来的位置重新亮起来 */
  const applyRestored = (photo: PhotoDto, memory: MemoryDto): number => {
    applyMemory(memory);
    const i = galaxy.add(photo);
    refreshPanel();
    refreshCounts();
    remember();
    return i;
  };

  /** 提示里的「撤销」：一组里有的可能已经被恢复过、或者已经永久删除了 */
  const restoreAll = async (ids: string[]) => {
    let last = -1, ok = 0, failed = 0, why = '';
    for (const id of ids) {
      try {
        const r = await api.restorePhoto(id);
        last = applyRestored(r.photo, r.memory);
        ok++;
      } catch (e) {
        failed++;
        why = e instanceof Error ? e.message : String(e);
      }
    }
    const target = last;
    const text = [ok ? (ok > 1 ? `已恢复 ${ok} 张照片` : '照片已恢复') : '', failed ? `${failed} 张没恢复成（${why}）` : ''].filter(Boolean).join('，');
    toast(shell.toasts, text, target >= 0 ? { action: { label: '去看看', onClick: () => goTo(target) } } : {});
  };

  const binNote = (by: string, id: string) =>
    tally('bin', by, id, (ids) => ({
      text: by === me?.member.id ? `${ids.length > 1 ? `${ids.length} 张照片` : '照片'}已放进回收站` : `${nameOf(by)} 把 ${ids.length} 张照片放进了回收站`,
      action: { label: '撤销', onClick: () => void restoreAll(ids) },
    }));

  const deleteShown = async () => {
    const p = galaxy.photos[galaxy.selectedIndex];
    if (fake || !p) return;
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再删');
    const ok = await confirmDialog({
      title: '删除这张照片？',
      text: `照片会放进回收站，${LIMITS.trashDays} 天内你们两个人都能在「星海设置 → 回收站」里恢复。Ta 那边这颗星也会熄灭。`,
      confirm: '删除',
      danger: true,
    });
    if (!ok) return;
    try {
      await api.deletePhoto(p.id);
      // 推送可能比回应先到、已经把星熄灭了，提示照样给
      applyDeleted(p.id);
      if (me) binNote(me.member.id, p.id);
    } catch (e) {
      toast(shell.toasts, `没删掉：${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const openTrash = () => {
    if (fake || trashView) return;
    if (offline) return void toast(shell.toasts, '现在连不上星海服务，连上以后再打开回收站');
    let last = -1;
    trashView = trashDialog({
      load: async () => (await api.trash()).photos,
      thumb: (id) => api.photoBlob(id, 't128'),
      restore: async (p) => {
        const r = await api.restorePhoto(p.id);
        last = applyRestored(r.photo, r.memory);
      },
      purge: async (p) => {
        await api.purgePhoto(p.id);
        void forgetThumbs(p.id);
      },
      nameOf,
      onClose: () => {
        trashView = null;
        const target = last;
        if (target >= 0) toast(shell.toasts, '恢复的照片回到星海里了', { action: { label: '去看看', onClick: () => goTo(target) } });
      },
    });
  };

  if (import.meta.env.DEV) Object.assign(window, { __starSea: { galaxy, api, sound } });
  if (fake || !me) return;

  // ── 实时点亮 ──
  let lit: { count: number; last: number; by: string; note: ReturnType<typeof toast> | null; timer?: ReturnType<typeof setTimeout> } | null = null;
  const partnerLit = (by: string, index: number) => {
    if (!lit || lit.by !== by) lit = { count: 0, last: index, by, note: null };
    lit.count++;
    lit.last = index;
    const name = members.get(by)?.displayName ?? 'Ta';
    const text = `${name} 刚点亮了 ${lit.count} 颗星`;
    const target = lit.last;
    if (lit.note) lit.note.done(text, { label: '去看看', onClick: () => goTo(target) }, 8000);
    else lit.note = toast(shell.toasts, text, { action: { label: '去看看', onClick: () => goTo(target) }, ms: 8000 });
    clearTimeout(lit.timer);
    lit.timer = setTimeout(() => (lit = null), 8000);
  };

  const onChange = (c: Change) => {
    seq = Math.max(seq, c.seq);
    remember();
    if (c.kind === 'photo.added') {
      applyMemory(c.memory);
      const known = galaxy.indexOf(c.photo.id) >= 0;
      const i = galaxy.add(c.photo);
      if (!known) {
        refreshCounts();
        if (c.by && c.by !== me.member.id) partnerLit(c.by, i);
      }
    } else if (c.kind === 'member.joined') {
      members.set(c.member.id, c.member);
      galaxy.setMember(c.member);
      if (c.member.id !== me.member.id) toast(shell.toasts, `${c.member.displayName} 加入了星海`);
    } else if (c.kind === 'memory.updated') {
      const before = memories.get(c.memory.id);
      if (!applyMemory(c.memory) || !c.by || c.by === me.member.id) return;
      const text = editNote(nameOf(c.by), dayText(c.memory.date), before ? fieldsOf(before) : null, fieldsOf(c.memory));
      // 正在编辑这段回忆的话，表单里已经提醒过了
      if (!text || editing?.memoryId === c.memory.id) return;
      // 正看着这段回忆就不用「去看看」了
      const shownNow = galaxy.selectedIndex >= 0 && galaxy.photos[galaxy.selectedIndex]?.memoryId === c.memory.id;
      const target = galaxy.firstOf(c.memory.id);
      toast(shell.toasts, text, { action: shownNow || target < 0 ? undefined : { label: '去看看', onClick: () => goTo(target) }, ms: 8000 });
    } else if (c.kind === 'space.updated') {
      applySpace(c.space, c.by ?? null, c.seq);
    } else if (c.kind === 'photo.deleted') {
      const lit = applyDeleted(c.photo.id);
      // 自己这台设备做的改动，回应里已经处理过了；别人做的（Ta，或者自己的另一台设备）才提示、刷新回收站
      if (c.by === me.member.id) return;
      refreshTrash();
      if (lit && c.by) binNote(c.by, c.photo.id);
    } else if (c.kind === 'photo.restored') {
      const k = galaxy.indexOf(c.photo.id), wasGone = k < 0 || galaxy.isGone(k);
      const i = applyRestored(c.photo, c.memory);
      if (c.by === me.member.id) return;
      refreshTrash();
      const by = c.by;
      if (wasGone && by) tally('back', by, c.photo.id, (ids) => ({ text: `${nameOf(by)} 从回收站恢复了 ${ids.length} 张照片`, action: { label: '去看看', onClick: () => goTo(i) } }));
    } else if (c.kind === 'photo.purged') {
      // 永久删除了：没收到放进回收站的那条（离线太久）也照样熄灭；这台设备上缓存的缩略图一起删掉
      applyDeleted(c.photoId);
      void forgetThumbs(c.photoId);
      if (c.by !== me.member.id) refreshTrash();
    }
  };
  api.subscribe(snap.seq, onChange, (state) => {
    if (state === 'unauthorized') {
      void forget().then(() => location.reload());
      return;
    }
    if (state === 'retrying' && offline) return;
    hud.setConnection(state);
    if (state !== 'open') return;
    // 接上了：之前断网没取到的缩略图再要一次；从离线缓存打开的，缺的变更由实时推送补上
    galaxy.pool.retryFailed();
    if (offline) {
      offline = false;
      offlineNote?.done('已经重新连上星海服务，星海会自动跟上最新的', { label: '刷新', onClick: () => location.reload() }, 10000);
    }
  });

  // ── 添照片 ──
  let busy = Promise.resolve();
  const upload = (files: File[]) =>
    (busy = busy.then(async () => {
      const note = toast(shell.toasts, `正在点亮 1 / ${files.length}…`, { sticky: true });
      let added = 0, dup = 0, back = 0, failed = 0, heicPending = 0, lastIndex = -1, lastError = '';
      for (const [k, file] of files.entries()) {
        note.update(`正在点亮 ${k + 1} / ${files.length}…`);
        try {
          const res = await api.upload(await uploadForm(file), (f) => note.progress((k + f) / files.length));
          if (res.restored) {
            lastIndex = applyRestored(res.photo, res.memory);
            back++;
          } else {
            applyMemory(res.memory);
            lastIndex = galaxy.add(res.photo);
            if (res.duplicate) dup++;
            else added++;
          }
          if (!res.photo.thumbs && isHeic(file)) heicPending++;
        } catch (e) {
          failed++;
          lastError = e instanceof Error ? e.message : String(e);
        }
        note.progress((k + 1) / files.length);
      }
      refreshCounts();
      const parts = [
        added ? `点亮了 ${added} 颗星` : '',
        back ? `${back} 张从回收站恢复了` : '',
        dup ? `${dup} 张已经在星海里了` : '',
        failed ? `${failed} 张没传上去（${lastError}）` : '',
        heicPending ? `${heicPending} 张 HEIC 暂时没有缩略图` : '',
      ].filter(Boolean);
      const target = lastIndex;
      note.done(parts.join('，') || '没有照片', target >= 0 ? { label: '去看看', onClick: () => goTo(target) } : undefined, failed ? 12000 : 7000);
    }));
}

void boot();

// PWA：只在生产构建里注册（开发时每次改代码都要更新缓存，碍事）；注册失败不影响使用
if (import.meta.env.PROD && 'serviceWorker' in navigator) {
  addEventListener('load', () => void navigator.serviceWorker.register('/sw.js').catch(() => undefined));
}
